import { Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { FC, FormEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg';
import { getTheme } from 'src/utils/get-theme';

import { usePasswordLoginSupabase } from '../hooks/use-password-login-supabase';
import { ReactComponent as ShowPasswordIcon } from '../../../assets/svg/show-pass.svg';

import { styles } from './styles/otp-form';

export const SupabasePasswordForm: FC<{
    email: string;
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    switchToOtp: () => void;
    switchToForgotPassword: () => void;
}> = (props) => {
    const { email, onEmailChange, switchToOtp, switchToForgotPassword } = props;
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { mutate: login, isLoading: isLoggingIn, error: loginError } = usePasswordLoginSupabase();
    const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        login({ email, password });
    };
    const { theme } = getTheme();

    const getErrorMessage = (): string | null => {
        if (!loginError) return null;

        switch (loginError.status) {
            case 400:
                return t('supabase_password_login_error_bad_credentials');
            case 429:
                return t('supabase_password_login_error_rate_limit');
            default:
                return t('supabase_password_login_error_generic');
        }
    };

    return (
        <Box css={styles.login}>
            <Box css={styles.loginContainer}>
                <Stack css={styles.card}>
                    <Box css={styles.headerBox}>
                        <Typography variant="h2" css={styles.headerLineHeight}>
                            {t('supabase_password_login_title')}
                        </Typography>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                            {t('supabase_password_login_subtitle')}
                        </Typography>
                    </Box>
                    {getErrorMessage() && (
                        <Stack direction="row" alignItems="center" css={styles.errorWrap} gap={1}>
                            <WarningIcon
                                width={theme.typography.h2.fontSize}
                                height={theme.typography.h2.fontSize}
                                color={theme.palette.error.main}
                            />
                            <Typography variant="body1" color={theme.palette.error.main}>
                                {getErrorMessage()}
                            </Typography>
                        </Stack>
                    )}
                    <form onSubmit={handleSubmit}>
                        <Box css={styles.inputsWrap}>
                            <TextField
                                value={email}
                                variant="standard"
                                onChange={onEmailChange}
                                type="email"
                                name="Email"
                                placeholder={t('supabase_email')}
                                id="email-input"
                                fullWidth
                                error={Boolean(loginError)}
                                css={styles.inputField}
                            />
                            <TextField
                                value={password}
                                variant="standard"
                                onChange={({ target }) => setPassword(target.value)}
                                type={showPassword ? 'text' : 'password'}
                                name="Password"
                                placeholder={t('supabase_password')}
                                id="password-input"
                                color={loginError ? 'error' : 'secondary'}
                                error={Boolean(loginError)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword((show) => !show)}
                                            >
                                                <ShowPasswordIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                css={styles.inputField}
                            />
                        </Box>
                        <Button
                            color="primary"
                            type="submit"
                            id="submit-button"
                            variant="outlined"
                            fullWidth
                            loading={isLoggingIn}
                            css={styles.button}
                        >
                            {t('supabase_password_login_submit')}
                        </Button>
                        <Box css={styles.passwordLoginSecondaryButtons}>
                            <Typography
                                variant="body2"
                                role="button"
                                css={styles.secondaryButton}
                                onClick={switchToForgotPassword}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        switchToForgotPassword();
                                    }
                                }}
                            >
                                {t('supabase_forgot_password')}
                            </Typography>
                            <Typography
                                variant="body2"
                                role="button"
                                css={styles.secondaryButton}
                                onClick={switchToOtp}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        switchToOtp();
                                    }
                                }}
                            >
                                {t('supabase_password_login_switch_to_otp')}
                            </Typography>
                        </Box>
                    </form>
                </Stack>
            </Box>
        </Box>
    );
};
